import React from "react";
import {
  Box,
  Typography,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  TextField,
  Button,
  Container,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import MainAppBar from "../../components/MainAppBar";
import CustomAlert from "../../components/CustomAlert/CustomAlert";
import InfoPopover from "../../../utils/informationButton";
import SellerLegalInfo from "../../components/sellerLegalInfo/sellerLegalInfo";
import TaxRateSelector from "./components/taxRateSelector";
import BuyerEmailMessageEditor from "./components/buyerEmailMessageEditor";
import TourEmailIconUpload from "./components/tourEmailIconUpload";
import {
  MAX_TOUR_PRICE,
  MIN_TOUR_PRICE,
} from "../../../constants/monetization";

const SellTourConfigurationView = (props) => {
  const { t } = useTranslation();
  const {
    classes,
    state,
    commissions,
    selectedTaxOption,
    isSaveDisabled,
    isLegalInfoComplete,
    isContactEmailValid,
    handleContactEmailChange,
    legalInfoRef,
    handleMonetizationChange,
    handlePriceChange,
    handleTaxRateChange,
    handleLegalFormChange,
    handleSubmitLegalInfo,
    handleConfirmLegalInfo,
    handleCancelLegalConfirm,
    handleToggleTaxForm,
    handleNewTaxRateChange,
    handleSubmitNewTaxRate,
    handleCancelConfirm,
    handleConfirmCreateTaxRate,
    handleSelectIconFile,
    handleDeleteIcon,
    handleBuyerEmailTextChange,
    handleSaveBuyerEmailText,
    handleSave,
  } = props;

  const formatAmount = (amount) =>
    amount || amount === 0 ? amount.toFixed(2) : "0.00";

  return (
    <>
      <MainAppBar
        headerText={t("sell_tour_configuration.title")}
        showHomeButton={true}
      />
      <Box className={classes.root}>
        <Container className={classes.mainContent} maxWidth={false}>
          <Box className={classes.container}>
            <Typography className={classes.title}>
              {t("sell_tour_configuration.title")} - {state.tourTitle}
            </Typography>
            <Box className={classes.form}>
              <div ref={legalInfoRef}>
                {state.legalInfo.loaded && (
                  <SellerLegalInfo
                    legalInfo={state.legalInfo}
                    formValues={state.legalForm}
                    onChange={handleLegalFormChange}
                    onSubmit={handleSubmitLegalInfo}
                    isSaving={state.isSavingLegalInfo}
                    highlighted={state.highlightLegalInfo}
                    missingFields={state.missingLegalFields}
                    isConfirmOpen={state.isLegalConfirmOpen}
                    onConfirm={handleConfirmLegalInfo}
                    onCancelConfirm={handleCancelLegalConfirm}
                  />
                )}
              </div>

              <Box className={classes.checkboxContainer}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.isMonetized}
                      onChange={handleMonetizationChange}
                      className={classes.checkbox}
                    />
                  }
                  label={
                    <Typography className={classes.checkboxLabel}>
                      {t("sell_tour_configuration.monetization_active")}
                    </Typography>
                  }
                />
              </Box>

              {state.isMonetized &&
                !isLegalInfoComplete &&
                state.legalInfo.loaded && (
                  <Typography className={classes.blockingHint}>
                    {t("sell_tour_configuration.error.legal_info_required")}
                  </Typography>
                )}

              {state.isMonetized && (
                <Box className={classes.priceContainer}>
                  <TaxRateSelector
                    taxRateOptions={state.taxRateOptions}
                    taxRateOptionId={state.taxRateOptionId}
                    onTaxRateChange={handleTaxRateChange}
                    isAddFormOpen={state.isTaxFormOpen}
                    onToggleAddForm={handleToggleTaxForm}
                    newTaxRate={state.newTaxRate}
                    onNewTaxRateChange={handleNewTaxRateChange}
                    onSubmitNewTaxRate={handleSubmitNewTaxRate}
                    isCreatingTaxRate={state.isCreatingTaxRate}
                    isConfirmOpen={state.isConfirmOpen}
                    onConfirmCreate={handleConfirmCreateTaxRate}
                    onCancelConfirm={handleCancelConfirm}
                    showMissingTaxWarning={state.showMissingTaxWarning}
                  />

                  <Box className={classes.labelWithInfo}>
                    <Typography className={classes.priceLabel}>
                      {t("sell_tour_configuration.contact_email")}
                    </Typography>
                    <InfoPopover
                      title={t("sell_tour_configuration.contact_email")}
                      text={t("sell_tour_configuration.contact_email_info")}
                    />
                  </Box>
                  <TextField
                    type="email"
                    value={state.contactEmail}
                    onChange={handleContactEmailChange}
                    required
                    variant="outlined"
                    placeholder={t(
                      "sell_tour_configuration.contact_email_placeholder"
                    )}
                    className={classes.priceInput}
                    error={!!state.contactEmail && !isContactEmailValid}
                    helperText={
                      state.contactEmail && !isContactEmailValid
                        ? t("sell_tour_configuration.error.contact_email_invalid")
                        : ""
                    }
                    inputProps={{ maxLength: 254 }}
                  />

                  <Typography className={classes.priceLabel}>
                    {t("sell_tour_configuration.price")}
                  </Typography>
                  <TextField
                    type="number"
                    value={state.price}
                    onChange={handlePriceChange}
                    required
                    variant="outlined"
                    className={classes.priceInput}
                    inputProps={{
                      min: MIN_TOUR_PRICE,
                      max: MAX_TOUR_PRICE,
                      step: 1,
                    }}
                    InputProps={{
                      endAdornment: "€",
                    }}
                  />
                  <Typography className={classes.priceHelper}>
                    {t("sell_tour_configuration.price_inclusive_helper")}
                  </Typography>

                  <Box className={classes.commissionsContainer}>
                    <Box className={classes.commissionItem}>
                      <Typography className={classes.commissionTitle}>
                        {t("sell_tour_configuration.after_commissions_ttc", {
                          amount: formatAmount(commissions.netTtc),
                        })}
                      </Typography>
                      <Typography className={classes.commissionSubtitle}>
                        {t("sell_tour_configuration.ttc_amount")}
                      </Typography>
                    </Box>

                    <Box className={classes.commissionItem}>
                      <Typography className={classes.commissionTitle}>
                        {commissions.netHt === null
                          ? "—"
                          : t("sell_tour_configuration.after_commissions", {
                              amount: formatAmount(commissions.netHt),
                            })}
                      </Typography>
                      <Typography className={classes.commissionSubtitle}>
                        {selectedTaxOption
                          ? t("sell_tour_configuration.net_amount", {
                              percent: selectedTaxOption.percentage,
                            })
                          : t("sell_tour_configuration.net_amount_no_tax")}
                      </Typography>
                    </Box>

                    <Box className={classes.commissionItem}>
                      <Typography className={classes.commissionTitle}>
                        {!state.price
                          ? "0.00€"
                          : state.price <= 10
                            ? t("sell_tour_configuration.commission_fixed", {
                                amount: formatAmount(commissions.platform),
                              })
                            : t("sell_tour_configuration.commission_percent", {
                                amount: formatAmount(commissions.platform),
                                percent: state.stripeConnectCommissionPercent,
                              })}
                      </Typography>
                      <Typography className={classes.commissionSubtitle}>
                        {t("sell_tour_configuration.platform_commission")}
                      </Typography>
                    </Box>

                    <Box className={classes.commissionItem}>
                      <Typography className={classes.commissionTitle}>
                        {state.price
                          ? t("sell_tour_configuration.commission_stripe", {
                              amount: formatAmount(commissions.stripe),
                            })
                          : "0.00€"}
                      </Typography>
                      <Typography className={classes.commissionSubtitle}>
                        {t("sell_tour_configuration.stripe_commission")}
                      </Typography>
                    </Box>

                    <Box className={classes.commissionInfo}>
                      <Typography className={classes.commissionText}>
                        {t("sell_tour_configuration.commission_stripe_vary")}
                        <a
                          href="https://stripe.com/en-fr/pricing"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.stripeLink}
                        >
                          {t("sell_tour_configuration.stripe_fees_info")}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              <Button
                variant="contained"
                className={classes.saveButton}
                onClick={handleSave}
                disabled={isSaveDisabled}
              >
                {state.isSaving ? (
                  <CircularProgress size={20} style={{ color: "white" }} />
                ) : (
                  t("sell_tour_configuration.save")
                )}
              </Button>

              {/* Le message a son propre endpoint et sa propre sauvegarde :
                  l'enregistrement de la monétisation redirige vers l'accueil,
                  ce qui empêcherait de voir le HTML nettoyé par le back. */}
              <Divider className={classes.sectionDivider} />

              <TourEmailIconUpload
                iconUrl={state.iconUrl}
                isUploading={state.isUploadingIcon}
                isDeleting={state.isDeletingIcon}
                onSelectFile={handleSelectIconFile}
                onDelete={handleDeleteIcon}
              />

              <Divider className={classes.sectionDivider} />

              <BuyerEmailMessageEditor
                value={state.buyerEmailCustomText}
                onChange={handleBuyerEmailTextChange}
                onSave={handleSaveBuyerEmailText}
                isSaving={state.isSavingBuyerEmailText}
              />
            </Box>
          </Box>
          <CustomAlert
            severity="error"
            message={state.errorMessage}
            duration={5000}
            onClose={props.resetErrorAlert}
            height={30}
          />

          <CustomAlert
            severity="success"
            message={state.successMessage}
            duration={3000}
            onClose={props.resetSuccessAlert}
            height={30}
          />
        </Container>
      </Box>
    </>
  );
};

export default SellTourConfigurationView;
